import * as CookieConsent from 'vanilla-cookieconsent';
import { InjectionToken } from '@angular/core';
import {
  CAT_NECESSARY,
  CAT_ANALYTICS,
  SERVICE_ANALYTICS_STORAGE,
  CAT_ADVERTISEMENT,
  SERVICE_AD_STORAGE,
  SERVICE_AD_USER_DATA,
  SERVICE_AD_PERSONALIZATION,
  CAT_FUNCTIONALITY,
  SERVICE_FUNCTIONALITY_STORAGE,
  SERVICE_PERSONALIZATION_STORAGE,
  CAT_SECURITY,
  SERVICE_SECURITY_STORAGE,
} from './constants';

export const CONSENT_CONFIG_DEFAULT: CookieConsent.CookieConsentConfig = {
  disablePageInteraction: true,
  revision: 4,
  cookie: {
    name: '23degrees_cookie',
    // domain: window.location.hostname,
    path: '/',
    expiresAfterDays: 182,
    sameSite: 'Lax',
    useLocalStorage: false,
  },
  guiOptions: {
    consentModal: {
      layout: 'cloud',
      position: 'middle center',
      flipButtons: false,
      equalWeightButtons: true,
    },
    preferencesModal: {
      layout: 'box',
      position: 'right',
      flipButtons: false,
      equalWeightButtons: true,
    },
  },
  categories: {
    [CAT_NECESSARY]: {
      enabled: true, // this category is enabled by default
      readOnly: true, // this category cannot be disabled
    },
    [CAT_ANALYTICS]: {
      enabled: false,
      autoClear: {
        cookies: [
          {
            name: /^_ga/, // regex: match all cookies starting with '_ga'
          },
          {
            name: '_gid', // string: exact cookie name
          },
        ],
      },
      // See: https://cookieconsent.orestbida.com/reference/configuration-reference.html#category-services
      services: {
        [SERVICE_ANALYTICS_STORAGE]: {
          label: 'Enables storage (such as cookies) related to analytics e.g. visit duration.',
        },
      },
    },
    [CAT_ADVERTISEMENT]: {
      enabled: false,
      services: {
        [SERVICE_AD_STORAGE]: {
          label: 'Enables storage (such as cookies) related to advertising.',
        },
        [SERVICE_AD_USER_DATA]: {
          label: 'Sets consent for sending user data related to advertising to Google.',
        },
        [SERVICE_AD_PERSONALIZATION]: {
          label: 'Sets consent for personalized advertising.',
        },
      },
    },
    // [CAT_FUNCTIONALITY]: {
    //   enabled: true,
    //   services: {
    //     [SERVICE_FUNCTIONALITY_STORAGE]: {
    //       label: 'Enables storage that supports the functionality of the website or app e.g. language settings.',
    //     },
    //     [SERVICE_PERSONALIZATION_STORAGE]: {
    //       label: 'Enables storage related to personalization e.g. video recommendations.',
    //     },
    //   },
    // },
    // [CAT_SECURITY]: {
    //   enabled: true,
    //   services: {
    //     [SERVICE_SECURITY_STORAGE]: {
    //       label: 'Enables storage related to security such as authentication functionality, fraud prevention, and other user protection.',
    //     },
    //   },
    // },
  },
  language: {
    default: 'en',
    autoDetect: 'document',
    translations: {
      en: {
        // See: https://support.google.com/tagmanager/answer/10718549?hl=en
        consentModal: {
          title: "Hello traveller, it's cookie time!",
          description:
            'We use cookies and similar technologies to provide you with the best user experience, improve our website, and display relevant content. You can adjust your preferences at any time.',
          acceptAllBtn: 'Accept all',
          acceptNecessaryBtn: 'Reject all',
          showPreferencesBtn: 'Manage preferences',
          footer: '<a href="/privacy-policy">Privacy Policy</a>\n<a href="/terms">Terms and conditions</a>',
        },
        preferencesModal: {
          title: 'Consent Preferences Center',
          acceptAllBtn: 'Accept all',
          acceptNecessaryBtn: 'Reject all',
          savePreferencesBtn: 'Save preferences',
          closeIconLabel: 'Close modal',
          serviceCounterLabel: 'Service|Services',
          sections: [
            {
              title: 'Cookie usage',
              description: 'We use cookies to ensure the basic functionalities of the website and to enhance your online experience.',
            },
            {
              title: 'Strictly necessary cookies',
              description: 'These cookies are essential for the proper functioning of the website, for example for user authentication.',
              linkedCategory: CAT_NECESSARY,
            },
            {
              title: 'Analytics',
              description:
                'Cookies used for analytics help collect data that allows services to understand how users interact with a particular service. These insights allow services both to improve content and to build better features that improve the user’s experience.',
              linkedCategory: CAT_ANALYTICS,
              cookieTable: {
                headers: {
                  name: 'Name',
                  domain: 'Service',
                  description: 'Description',
                  expiration: 'Expiration',
                },
                body: [
                  {
                    name: '_ga',
                    domain: 'Google Analytics',
                    description: 'Cookie set by <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                    expiration: 'Expires after 12 days',
                  },
                  {
                    name: '_gid',
                    domain: 'Google Analytics',
                    description: 'Cookie set by <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                    expiration: 'Session',
                  },
                ],
              },
            },
            {
              title: 'Advertising',
              description:
                'Google uses cookies for advertising, including serving and rendering ads, personalizing ads (depending on your ad settings at <a href="https://g.co/adsettings">g.co/adsettings</a>), limiting the number of times an ad is shown to a user, muting ads you have chosen to stop seeing, and measuring the effectiveness of ads.',
              linkedCategory: CAT_ADVERTISEMENT,
            },
            // {
            //   title: 'Functionality',
            //   description:
            //     'Cookies used for functionality allow users to interact with a service or site to access features that are fundamental to that service. Things considered fundamental to the service include preferences like the user’s choice of language, product optimizations that help maintain and improve a service, and maintaining information relating to a user’s session, such as the content of a shopping cart.',
            //   linkedCategory: CAT_FUNCTIONALITY,
            // },
            // {
            //   title: 'Security',
            //   description: 'Cookies used for security authenticate users, prevent fraud, and protect users as they interact with a service.',
            //   linkedCategory: CAT_SECURITY,
            // },
            {
              title: 'More information',
              description:
                'For any queries in relation to the policy on cookies and your choices, please <a href="https://www.example.com/contacts">contact us</a>.',
            },
          ],
        },
      },
      de: {
        // See: https://support.google.com/tagmanager/answer/10718549?hl=en
        consentModal: {
          title: 'Hallo Reisende, es ist Kekszeit!',
          description:
            'Wir verwenden Cookies und ähnliche Technologien, um Ihnen ein optimales Nutzererlebnis zu bieten, unsere Website zu verbessern und relevante Inhalte anzuzeigen. Sie können Ihre Präferenzen jederzeit anpassen.',
          acceptAllBtn: 'Alle akzeptieren',
          acceptNecessaryBtn: 'Alle ablehnen',
          showPreferencesBtn: 'Einstellungen verwalten',
          footer: '<a href="/privacy-policy">Datenschutz</a>\n<a href="/terms">Bedingungen und Konditionen</a>',
        },
        preferencesModal: {
          title: 'Präferenzen für die Zustimmung',
          acceptAllBtn: 'Alle akzeptieren',
          acceptNecessaryBtn: 'Alle ablehnen',
          savePreferencesBtn: 'Einstellungen speichern',
          closeIconLabel: 'Modal schließen',
          serviceCounterLabel: 'Dienstleistungen',
          sections: [
            {
              title: 'Verwendung von Cookies',
              description: 'We use cookies to ensure the basic functionalities of the website and to enhance your online experience.',
            },
            {
              title: 'Streng Notwendige Cookies <span class="pm__badge">Immer Aktiviert</span>',
              description: 'These cookies are essential for the proper functioning of the website, for example for user authentication.',
              linkedCategory: CAT_NECESSARY,
            },
            {
              title: 'Analytische Cookies',
              description:
                'Cookies used for analytics help collect data that allows services to understand how users interact with a particular service. These insights allow services both to improve content and to build better features that improve the user’s experience.',
              linkedCategory: CAT_ANALYTICS,
              cookieTable: {
                headers: {
                  name: 'Name',
                  domain: 'Service',
                  description: 'Description',
                  expiration: 'Expiration',
                },
                body: [
                  {
                    name: '_ga',
                    domain: 'Google Analytics',
                    description: 'Cookie set by <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                    expiration: 'Expires after 12 days',
                  },
                  {
                    name: '_gid',
                    domain: 'Google Analytics',
                    description: 'Cookie set by <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                    expiration: 'Session',
                  },
                ],
              },
            },
            {
              title: 'Werbung Cookies',
              description:
                'Google uses cookies for advertising, including serving and rendering ads, personalizing ads (depending on your ad settings at <a href="https://g.co/adsettings">g.co/adsettings</a>), limiting the number of times an ad is shown to a user, muting ads you have chosen to stop seeing, and measuring the effectiveness of ads.',
              linkedCategory: CAT_ADVERTISEMENT,
            },
            // {
            //   title: 'Functionality',
            //   description:
            //     'Cookies used for functionality allow users to interact with a service or site to access features that are fundamental to that service. Things considered fundamental to the service include preferences like the user’s choice of language, product optimizations that help maintain and improve a service, and maintaining information relating to a user’s session, such as the content of a shopping cart.',
            //   linkedCategory: CAT_FUNCTIONALITY,
            // },
            // {
            //   title: 'Security',
            //   description: 'Cookies used for security authenticate users, prevent fraud, and protect users as they interact with a service.',
            //   linkedCategory: CAT_SECURITY,
            // },
            {
              title: 'More information',
              description:
                'For any queries in relation to the policy on cookies and your choices, please <a href="https://www.example.com/contacts">contact us</a>.',
            },
          ],
        },
      },
    },
  },
};

export const CONSENT_SECTION_TRANSLATION_FIELDS = [
  {
    key: 'consent.preferences.section.analytics.services.analytics_storage.label',
    field: 'analytics.services.analytics_storage.label',
  },
  {
    key: 'consent.preferences.section.advertisement.services.ad_storage.label',
    field: 'advertisement.services.ad_storage.label',
  },
  {
    key: 'consent.preferences.section.advertisement.services.ad_user_data.label',
    field: 'advertisement.services.ad_user_data.label',
  },
  {
    key: 'consent.preferences.section.advertisement.services.ad_personalization.label',
    field: 'advertisement.services.ad_personalization.label',
  },
];
export const CONSENT_TRANSLATION_FIELDS = [
  {
    key: 'consent.modal.title',
    field: 'consentModal.title',
  },
  {
    key: 'consent.modal.description',
    field: 'consentModal.description',
  },
  {
    key: 'consent.modal.acceptAllBtn',
    field: 'consentModal.acceptAllBtn',
  },
  {
    key: 'consent.modal.acceptNecessaryBtn',
    field: 'consentModal.acceptNecessaryBtn',
  },
  {
    key: 'consent.modal.showPreferencesBtn',
    field: 'consentModal.showPreferencesBtn',
  },
  {
    key: 'consent.modal.footer',
    field: 'consentModal.footer',
  },
  {
    key: 'consent.preferences.title',
    field: 'preferencesModal.title',
  },
  {
    key: 'consent.preferences.acceptAllBtn',
    field: 'preferencesModal.acceptAllBtn',
  },
  {
    key: 'consent.preferences.acceptNecessaryBtn',
    field: 'preferencesModal.acceptNecessaryBtn',
  },
  {
    key: 'consent.preferences.savePreferencesBtn',
    field: 'preferencesModal.savePreferencesBtn',
  },
  {
    key: 'consent.preferences.serviceCounterLabel',
    field: 'preferencesModal.serviceCounterLabel',
  },
  {
    key: 'consent.preferences.header',
    field: 'preferencesModal.sections[0].title',
  },
  {
    key: 'consent.preferences.description',
    field: 'preferencesModal.sections[0].description',
  },
  {
    key: 'consent.preferences.section.necessary.title',
    field: 'preferencesModal.sections[1].title',
  },
  {
    key: 'consent.preferences.section.necessary.description',
    field: 'preferencesModal.sections[1].description',
  },
  {
    key: 'consent.preferences.section.analytics.title',
    field: 'preferencesModal.sections[2].title',
  },
  {
    key: 'consent.preferences.section.analytics.description',
    field: 'preferencesModal.sections[2].description',
  },
  {
    key: 'consent.preferences.section.table.headers.name',
    field: 'preferencesModal.sections[2].cookieTable.headers.name',
  },
  {
    key: 'consent.preferences.section.table.headers.domain',
    field: 'preferencesModal.sections[2].cookieTable.headers.domain',
  },
  {
    key: 'consent.preferences.section.table.headers.description',
    field: 'preferencesModal.sections[2].cookieTable.headers.description',
  },
  {
    key: 'consent.preferences.section.table.headers.expiration',
    field: 'preferencesModal.sections[2].cookieTable.headers.expiration',
  },
  {
    key: 'consent.preferences.section.analytics.ga.name',
    field: 'preferencesModal.sections[2].cookieTable.body[0].name',
  },
  {
    key: 'consent.preferences.section.analytics.ga.domain',
    field: 'preferencesModal.sections[2].cookieTable.body[0].domain',
  },
  {
    key: 'consent.preferences.section.analytics.ga.description',
    field: 'preferencesModal.sections[2].cookieTable.body[0].description',
  },
  {
    key: 'consent.preferences.section.analytics.ga.expiration',
    field: 'preferencesModal.sections[2].cookieTable.body[0].expiration',
  },

  {
    key: 'consent.preferences.section.analytics.gid.name',
    field: 'preferencesModal.sections[2].cookieTable.body[1].name',
  },
  {
    key: 'consent.preferences.section.analytics.gid.domain',
    field: 'preferencesModal.sections[2].cookieTable.body[1].domain',
  },
  {
    key: 'consent.preferences.section.analytics.gid.description',
    field: 'preferencesModal.sections[2].cookieTable.body[1].description',
  },
  {
    key: 'consent.preferences.section.analytics.gid.expiration',
    field: 'preferencesModal.sections[2].cookieTable.body[1].expiration',
  },

  {
    key: 'consent.preferences.section.advertisement.title',
    field: 'preferencesModal.sections[3].title',
  },
  {
    key: 'consent.preferences.section.advertisement.description',
    field: 'preferencesModal.sections[3].description',
  },
  {
    key: 'consent.preferences.section.moreInfo.title',
    field: 'preferencesModal.sections[4].title',
  },
  {
    key: 'consent.preferences.section.moreInfo.description',
    field: 'preferencesModal.sections[4].description',
  },
];

export const CONSENT_CONFIG = new InjectionToken<Partial<CookieConsent.CookieConsentConfig>>('CONSENT_CONFIG', {
  providedIn: 'root',
  factory: () => CONSENT_CONFIG_DEFAULT,
});

export const CONSENT_ENABLED = new InjectionToken<boolean>('CONSENT_ENABLED', {
  providedIn: 'root',
  factory: () => false,
});
